import {
  activityResponseSchema,
  availabilityResponseSchema,
  checkoutStartedSchema,
  guestBookingSchema,
  guestCancelledSchema,
  guestExtensionStartedSchema,
  authMethodsSchema,
  mapResponseSchema,
  reservationSchema,
  reservationsResponseSchema,
  spotsResponseSchema,
  placeDetailsSchema,
  placeSuggestionsSchema,
  tenantSchema,
  tenantsResponseSchema,
  healthResponseSchema,
  layoutDraftSchema,
  myMembershipSchema,
  sessionResponseSchema,
  teamMemberSchema,
  teamResponseSchema,
  venueSettingsSchema,
  type ActivityResponse,
  type AuthMethods,
  type AvailabilityResponse,
  type BookingLookup,
  type CheckoutRequest,
  type CheckoutStarted,
  type GuestBooking,
  type GuestCancelled,
  type GuestExtension,
  type GuestExtensionStarted,
  type HealthResponse,
  type BulkSpotFlags,
  type BulkStatusChange,
  type InviteRequest,
  type Language,
  type LayoutDraft,
  type MapResponse,
  type MemberUpdate,
  type MyMembership,
  type OwnerRegistration,
  type PlaceDetails,
  type PlaceSuggestions,
  type Reservation,
  type ReservationRequest,
  type ReservationsResponse,
  type SessionResponse,
  type SpotsResponse,
  type TeamMember,
  type TeamResponse,
  type Tenant,
  type TenantsResponse,
  type VenueProfile,
  type VenueSettings,
  type VenueSettingsUpdate,
} from '@kratispot/shared';
import { z, type ZodType } from 'zod';

// Empty base means same-origin: the Vite dev proxy and Caddy both mount /api.
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

const BASE = API_BASE;

// detail is the server's own wording, which names the spots in the way. §18
// body carries the server's own reason, which the screen translates. §15, §19
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string | null = null,
    readonly body: unknown = null,
  ) {
    super(detail ?? `api responded ${status}`);
  }
}

async function refusalOf(response: Response): Promise<{ detail: string | null; body: unknown }> {
  const body: unknown = await response.json().catch(() => null);
  const said = (body as { message?: unknown } | null)?.message;
  return { detail: typeof said === 'string' ? said : null, body };
}

type Options = {
  method?: string;
  body?: unknown;
  // Multipart carries its own content type with a boundary; JSON is the default.
  form?: FormData;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  cache?: RequestCache;
};

async function call<T>(path: string, schema: ZodType<T> | null, options: Options = {}): Promise<T> {
  const response = await fetch(`${BASE}/api${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    signal: options.signal,
    ...(options.cache === undefined ? {} : { cache: options.cache }),
    headers: {
      ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...options.headers,
    },
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  });
  if (!response.ok) {
    const refusal = await refusalOf(response);
    throw new ApiError(response.status, refusal.detail, refusal.body);
  }
  if (schema === null) return undefined as T;
  return schema.parse(await response.json());
}

export function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return call('/health', healthResponseSchema, { signal });
}

export function fetchAuthMethods(signal?: AbortSignal): Promise<AuthMethods> {
  return call('/auth/methods', authMethodsSchema, { signal });
}

export function fetchSession(signal?: AbortSignal): Promise<SessionResponse> {
  return call('/auth/session', sessionResponseSchema, { signal });
}

// The mail follows the language on screen: no venue is chosen yet, so nothing
// else knows which one the person asking reads. §19
export function requestMagicLink(email: string, language: Language): Promise<void> {
  return call('/auth/magic-link', null, { method: 'POST', body: { email, language } });
}

export function logout(): Promise<void> {
  return call('/auth/logout', null, { method: 'POST' });
}

export function fetchMyMembership(venueId: string, signal?: AbortSignal): Promise<MyMembership> {
  return call(`/venues/${venueId}/me`, myMembershipSchema, { signal });
}

export function fetchVenueMap(
  venueId: string,
  date: string | null,
  signal?: AbortSignal,
): Promise<MapResponse> {
  const query = date === null ? '' : `?date=${encodeURIComponent(date)}`;
  return call(`/venues/${venueId}/map${query}`, mapResponseSchema, { signal });
}

export function setSpotsStatus(venueId: string, change: BulkStatusChange): Promise<SpotsResponse> {
  return call(`/venues/${venueId}/spots/status`, spotsResponseSchema, {
    method: 'POST',
    body: change,
  });
}

export function setSpotsStaffOnly(venueId: string, flags: BulkSpotFlags): Promise<SpotsResponse> {
  return call(`/venues/${venueId}/spots`, spotsResponseSchema, { method: 'PATCH', body: flags });
}

export function createReservation(
  venueId: string,
  request: ReservationRequest,
): Promise<ReservationsResponse> {
  return call(`/venues/${venueId}/reservations`, reservationsResponseSchema, {
    method: 'POST',
    body: request,
  });
}

// Cancel, check-in, restore: one booking, one verb, the same answer. §10
export function actOnReservation(
  venueId: string,
  reservationId: string,
  action: 'cancel' | 'check-in' | 'restore',
): Promise<ReservationsResponse> {
  return call(
    `/venues/${venueId}/reservations/${reservationId}/${action}`,
    reservationsResponseSchema,
    {
      method: 'POST',
      body: {},
    },
  );
}

export function extendReservation(
  venueId: string,
  reservationId: string,
  holdMinutes: number,
): Promise<ReservationsResponse> {
  return call(
    `/venues/${venueId}/reservations/${reservationId}/extend`,
    reservationsResponseSchema,
    {
      method: 'POST',
      body: { holdMinutes },
    },
  );
}

const reservationsInRangeSchema = z.object({ reservations: z.array(reservationSchema) });

export function fetchReservations(
  venueId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<{ reservations: Reservation[] }> {
  return call(`/venues/${venueId}/reservations?from=${from}&to=${to}`, reservationsInRangeSchema, {
    signal,
  });
}

export function fetchActivity(venueId: string, signal?: AbortSignal): Promise<ActivityResponse> {
  return call(`/venues/${venueId}/reservations/activity`, activityResponseSchema, { signal });
}

export function fetchVenueSettings(venueId: string, signal?: AbortSignal): Promise<VenueSettings> {
  return call(`/venues/${venueId}/settings`, venueSettingsSchema, { signal });
}

export function saveVenueSettings(
  venueId: string,
  update: VenueSettingsUpdate,
): Promise<VenueSettings> {
  return call(`/venues/${venueId}/settings`, venueSettingsSchema, { method: 'PUT', body: update });
}

export function fetchVenueLayout(venueId: string, signal?: AbortSignal): Promise<LayoutDraft> {
  return call(`/venues/${venueId}/layout`, layoutDraftSchema, { signal });
}

export function saveVenueLayout(venueId: string, draft: LayoutDraft): Promise<LayoutDraft> {
  return call(`/venues/${venueId}/layout`, layoutDraftSchema, { method: 'PUT', body: draft });
}

export function fetchTeam(venueId: string, signal?: AbortSignal): Promise<TeamResponse> {
  return call(`/venues/${venueId}/members`, teamResponseSchema, { signal });
}

export function inviteMember(venueId: string, invite: InviteRequest): Promise<TeamMember> {
  return call(`/venues/${venueId}/members`, teamMemberSchema, { method: 'POST', body: invite });
}

export function updateMember(
  venueId: string,
  membershipId: string,
  update: MemberUpdate,
): Promise<TeamMember> {
  return call(`/venues/${venueId}/members/${membershipId}`, teamMemberSchema, {
    method: 'PATCH',
    body: update,
  });
}

export function removeMember(venueId: string, membershipId: string): Promise<void> {
  return call(`/venues/${venueId}/members/${membershipId}`, null, { method: 'DELETE' });
}

export function fetchTenants(signal?: AbortSignal): Promise<TenantsResponse> {
  return call('/platform/venues', tenantsResponseSchema, { signal });
}

export function createVenue(profile: VenueProfile): Promise<Tenant> {
  return call('/platform/venues', tenantSchema, { method: 'POST', body: profile });
}

export function updateVenue(venueId: string, profile: VenueProfile): Promise<Tenant> {
  return call(`/platform/venues/${venueId}`, tenantSchema, { method: 'PATCH', body: profile });
}

// §3: an owner is taken off the venue from the console that put them on it.
export function removeOwner(venueId: string, membershipId: string): Promise<void> {
  return call(`/platform/venues/${venueId}/owners/${membershipId}`, null, { method: 'DELETE' });
}

// §3: OSM is asked from the server, so its usage policy is one place's to keep.
export function searchPlaces(query: string, signal?: AbortSignal): Promise<PlaceSuggestions> {
  return call(`/platform/places?q=${encodeURIComponent(query)}`, placeSuggestionsSchema, {
    signal,
  });
}

// §3: a pasted Google Maps link, which the server is what follows.
export function placeFromLink(url: string): Promise<PlaceDetails> {
  return call(`/platform/places/link?url=${encodeURIComponent(url)}`, placeDetailsSchema);
}

// §3: a logo is uploaded, downsized by the browser before it leaves it.
export function uploadVenueLogo(venueId: string, logo: Blob): Promise<Tenant> {
  const form = new FormData();
  form.append('logo', logo, 'logo');
  return call(`/platform/venues/${venueId}/logo`, tenantSchema, { method: 'PUT', form });
}

export function removeVenueLogo(venueId: string): Promise<Tenant> {
  return call(`/platform/venues/${venueId}/logo`, tenantSchema, { method: 'DELETE' });
}

// §18: an owner drawing a floor starts from another they may draw.
export function copyVenueLayout(venueId: string, fromVenueId: string): Promise<LayoutDraft> {
  return call(`/venues/${venueId}/layout/copy`, layoutDraftSchema, {
    method: 'POST',
    body: { fromVenueId },
  });
}

// §4: the team screen refuses the owner role, so the name is corrected here.
export function renameOwner(
  venueId: string,
  membershipId: string,
  name: string,
): Promise<TeamMember> {
  return call(`/platform/venues/${venueId}/owners/${membershipId}`, teamMemberSchema, {
    method: 'PATCH',
    body: { name },
  });
}

export function registerOwner(
  venueId: string,
  registration: OwnerRegistration,
): Promise<TeamMember> {
  return call(`/platform/venues/${venueId}/owner`, teamMemberSchema, {
    method: 'POST',
    body: registration,
  });
}

// The public surface: no session, no venue id, and a slug in the path. §12
export function fetchAvailability(
  slug: string,
  date: string | null,
  signal?: AbortSignal,
): Promise<AvailabilityResponse> {
  const query = date === null ? '' : `?date=${encodeURIComponent(date)}`;
  // Past the browser's cache, or a nudge repaints a floor it already holds.
  return call(`/public/venues/${slug}/availability${query}`, availabilityResponseSchema, {
    signal,
    cache: 'no-store',
  });
}

export function startCheckout(slug: string, request: CheckoutRequest): Promise<CheckoutStarted> {
  return call(`/public/venues/${slug}/checkout`, checkoutStartedSchema, {
    method: 'POST',
    body: request,
  });
}

// A POST, so a booking code and an address never travel in a URL. §12
export function lookupBooking(slug: string, lookup: BookingLookup): Promise<GuestBooking> {
  return call(`/public/venues/${slug}/bookings/lookup`, guestBookingSchema, {
    method: 'POST',
    body: lookup,
  });
}

export function extendBooking(
  slug: string,
  request: GuestExtension,
): Promise<GuestExtensionStarted> {
  return call(`/public/venues/${slug}/bookings/extend`, guestExtensionStartedSchema, {
    method: 'POST',
    body: request,
  });
}

export function cancelBooking(slug: string, lookup: BookingLookup): Promise<GuestCancelled> {
  return call(`/public/venues/${slug}/bookings/cancel`, guestCancelledSchema, {
    method: 'POST',
    body: lookup,
  });
}

// The fake provider's page; the route answers only where it is running. §13
export function settleFakeCheckout(
  providerRef: string,
  outcome: 'paid' | 'failed' | 'cancelled',
): Promise<void> {
  return call(`/payments/fake/${encodeURIComponent(providerRef)}/${outcome}`, null, {
    method: 'POST',
    body: {},
  });
}

// The API redirects here itself; the browser follows it as a top-level GET.
export function googleSignInUrl(invite?: string): string {
  const query = invite === undefined ? '' : `?invite=${encodeURIComponent(invite)}`;
  return `${BASE}/api/auth/google/start${query}`;
}

export function magicLinkCallbackUrl(token: string): string {
  return `${BASE}/api/auth/magic-link/callback?token=${encodeURIComponent(token)}`;
}
